/**
 * ActivityUpload Component
 *
 * Features:
 * - Manages the activity upload workflow, including file upload, title assignment, and status display.
 * - Integrates with Redux actions for S3 credentials and homework upload.
 * - Uses i18next for translations.
 * - Utilizes Material-UI Button and Icon components for UI.
 * - Handles navigation and status via props.
 * - Uses CSS modules for styling.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScreenShareicon from '@mui/icons-material/ScreenShare';
import DoneIcon from '@mui/icons-material/Done';

import Loading from 'components/UI/Loading/Loading';
import StepThree from './StepThree/StepThree';
import StepTwo from './StepTwo/StepTwo';
import StepOne from './StepOne/StepOne';

import {
  getCredentialsUploadEvaluationS3,
  uploadHomework
} from 'store/lesson-actions';
import classes from './ActivityUpload.module.css';

const ActivityUpload = props => {
  const { t } = useTranslation(['lesson']);
  // State to indicate if the upload process is loading
  const [isLoading, setIsLoading] = useState(false);
  // State to indicate if the activity was uploaded successfully
  const [uploadActivityStatus, setUploadActivityStatus] =
    useState(false);
  // State to enable/disable the upload button
  const [uploadBtn, setUploadButton] = useState(true);
  // State to store the activity title
  const [activityTitle, setActivityTitle] = useState('');
  // State to store the lesson ID
  const [idLesson] = useState(props.idLesson);
  // State to store the uploaded file
  const [archive, setArchive] = useState(false);

  useEffect(() => {
    if (activityTitle.length > 0 && archive) {
      setUploadButton(false);
    } else {
      setUploadButton(true);
    }
  }, [archive, activityTitle]);

  const uploadButtonEnabledHandler = status => {
    setUploadButton(status);
  };

  const uploadHandler = async data => {
    setIsLoading(true);
    console.log(data);

    const credentialsUploadEvaluation =
      await getCredentialsUploadEvaluationS3(
        idLesson,
        activityTitle
      );
    if (credentialsUploadEvaluation) {
      const uploadEvaluation = await uploadHomework(
        credentialsUploadEvaluation.credentials,
        archive
      );
      if (uploadEvaluation) {
        setUploadActivityStatus(true);
        setIsLoading(false);
      }
    }
  };

  const activityTitleHandler = title => {
    setActivityTitle(title);
  };

  const archiveHandler = file => {
    setArchive(file);
  };

  return (
    <>
      <div className={classes.activityContainer}>
        {!uploadActivityStatus ? (
          <div className={classes.contentActivities}>
            {/* Props passed to StepOne:
                - uploadButtonEnabled: uploadButtonEnabledHandler (function)
                - archiveHandler: archiveHandler (function)
            */}
            <StepOne
              uploadButtonEnabled={uploadButtonEnabledHandler}
              archiveHandler={archiveHandler}
            />
            {/* Props passed to StepTwo:
                - activityTitleHandler: activityTitleHandler (function)
            */}
            <StepTwo activityTitleHandler={activityTitleHandler} />
            {/* No props passed to StepThree */}
            <StepThree />
          </div>
        ) : (
          <div className={classes.uploadActivityDoneContainer}>
            <DoneIcon className={classes.doneIcon} />
            <p>{t('uploaded_file_successfully')}</p>
            <Button
              className={classes.nextBtn}
              onClick={props.returnVideo}
            >
              {t('continue')}
            </Button>
          </div>
        )}
      </div>
      <aside className={classes.btnsContainer}>
        {!isLoading ? (
          !uploadActivityStatus && (
            <>
              <Button
                onClick={props.changeView}
                className={classes.nextBtn}
              >
                {t('instructions')} <AssignmentIcon />
              </Button>
              <Button
                disabled={uploadBtn}
                onClick={uploadHandler}
                className={classes.nextBtn}
              >
                {t('save_and_send')} <ScreenShareicon />
              </Button>
            </>
          )
        ) : (
          <>
            <Loading />
          </>
        )}
      </aside>
    </>
  );
};

ActivityUpload.propTypes = {
  /**
   * Function to change the view
   */
  changeView: PropTypes.func,
  /**
   * Course ID
   */
  idCourse: PropTypes.number,
  /**
   * Lesson ID
   */
  idLesson: PropTypes.number,
  /**
   * Function to return to the video
   */
  returnVideo: PropTypes.func,
  /**
   * Function to change the homework status
   */
  changeHomeworkStatus: PropTypes.func
};

ActivityUpload.defaultProps = {
  changeView: () => {},
  idCourse: null,
  idLesson: null,
  returnVideo: () => {},
  changeHomeworkStatus: () => {}
};

export default ActivityUpload;
